import React, { useRef, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Download } from 'lucide-react';
import { generatePDF } from '../utils/generatePDF';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Session F (Task F2) — mirrors ManualInvoiceLineItem.js's REFERENCE_LABELS
// so the client-facing PDF prints the right label without re-deriving it
// server-side. Misc carries its own agent-chosen label in referenceLabel.
const REFERENCE_LABELS = {
  Flight: 'PNR',
  Hotel: 'HCN',
  Visa: 'Application/Reference No.',
  Activity: 'Booking Reference',
  Tours: 'Booking Reference',
};

const InvoicePreview = ({ invoice, onBack, printMode = false }) => {
  const printRef = useRef(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [searchParams] = useSearchParams();
  // Phase C4 — per-split PDF mode. When `?split=<index>` is on the URL we
  // filter the entire render to that single traveller's row.
  const splitIndexParam = searchParams.get('split');
  const inv = invoice || {};
  // Read splits directly (not via useMemo) so a parent-state swap on first
  // navigation immediately re-renders the splits table. Phase 12 fix for
  // the "refresh-required" race observed in iter37.
  const allSplits = Array.isArray(inv.splits) ? inv.splits : [];
  // Bugfix: searchParams.get returns null for an absent key, but
  // `Number(null) === 0` (truthy + finite) which used to silently activate
  // split-mode at index 0. Guard with an explicit string check.
  const activeSplitIndex = (() => {
    if (splitIndexParam === null || splitIndexParam === '') return null;
    const n = Number(splitIndexParam);
    if (!Number.isFinite(n) || n < 0 || n >= allSplits.length) return null;
    return n;
  })();
  const activeSplit = activeSplitIndex !== null ? allSplits[activeSplitIndex] : null;
  const company = inv.company || {};
  // When rendering a per-split PDF, override the bill-to block with the
  // split row's traveller name + PAN. The DB-stored client survives for
  // anyone hitting the unfiltered URL.
  // The UCC used to be left out of this override, so a per-traveller PDF
  // printed the split's name and PAN next to the LEAD traveller's UCC. Splits
  // carry their own `ucc`, so use it — and fall back to the stored client only
  // when the row has none, rather than printing a UCC that contradicts the PAN.
  const client = activeSplit
    ? {
        ...(inv.client || {}),
        name: activeSplit.travellerName || (inv.client || {}).name,
        panCard: activeSplit.panCard || '',
        displayUCC: activeSplit.ucc || (inv.client || {}).displayUCC || '',
      }
    : (inv.client || {});
  const items = inv.items || [];
  const bank = inv.bankDetails || {};
  // If GST is zero (e.g. proforma / exempt), hide the HSN/SAC column entirely.
  const showHsn = Number(inv.gstRate) > 0;

  // NEW Playwright-backed renderer (Phase 6 migration, Feb 2026).
  // Same auth-forwarding pattern as the voucher + itinerary migrations:
  // generatePDF() POSTs to /api/pdf/render with the protected invoice
  // preview URL. The util ships httpOnly cookies (credentials:'include')
  // AND the localStorage Bearer token in the body so the headless Chromium
  // can replay auth on first paint.
  const handleDownloadPDF = async () => {
    setPdfLoading(true);
    try {
      const invoiceUrl = `${window.location.origin}/invoice-preview/${encodeURIComponent(inv.invoiceNumber)}`;
      const safeRef = String(inv.invoiceNumber || '').replace(/[^a-zA-Z0-9-_]/g, '-');
      const safeName = (client.name || '').replace(/[^a-zA-Z0-9-_]/g, '-').slice(0, 30);
      const filename = safeName
        ? `${safeRef}-${safeName}-Invoice.pdf`
        : `${safeRef}-Invoice.pdf`;
      const stamp = inv.updatedAt || '';
      await generatePDF({
        pageUrl: invoiceUrl,
        filename,
        waitUntil: 'load',
        waitSelector: '[data-testid="invoice-preview-page"]',
        cacheKey: stamp ? `invoice:${inv.invoiceNumber}:${stamp}` : null,
      });
      toast.success('PDF downloaded');
    } catch (err) {
      console.error('Invoice PDF failed:', err);
      toast.error('PDF generation failed');
    } finally {
      setPdfLoading(false);
    }
  };

  /* OLD RENDERER — replaced by Playwright (Phase 6 migration, Feb 2026).
     Kept commented out for emergency rollback only. Do not delete.

  const handleDownloadPDF = async () => {
    const el = printRef.current;
    if (!el) return;
    try {
      const html2pdf = (await import('html2pdf.js')).default;
      await html2pdf().set({
        margin: [8, 8, 8, 8],
        filename: `${inv.invoiceNumber}_GST_Invoice.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      }).from(el).save();
      toast.success('PDF downloaded');
    } catch (err) { toast.error('PDF generation failed'); }
  };
  */

  const displayTotals = useMemo(() => {
    if (!activeSplit) {
      return {
        baseCost: inv.baseCost,
        gstAmount: inv.gstAmount,
        cgstAmount: inv.cgstAmount,
        sgstAmount: inv.sgstAmount,
        igstAmount: inv.igstAmount,
        tcsAmount: inv.tcsAmount,
        subtotal: inv.subtotal,
        totalAmount: inv.totalAmount,
      };
    }
    // Pro-rate CGST/SGST/IGST from the invoice-level rates × split amount
    // so each line in the per-split PDF stays consistent with the totals.
    const gst = Number(activeSplit.gstAmount || 0);
    const tcs = Number(activeSplit.tcsAmount || 0);
    const base = Number(activeSplit.amount || 0);
    const isIntra = (inv.supplyType || 'interstate') === 'intrastate';
    return {
      baseCost: base,
      gstAmount: gst,
      cgstAmount: isIntra ? gst / 2 : 0,
      sgstAmount: isIntra ? gst / 2 : 0,
      igstAmount: isIntra ? 0 : gst,
      tcsAmount: tcs,
      subtotal: base + gst,
      totalAmount: base + gst + tcs,
    };
  }, [activeSplit, inv]);

  const amountInWords = numberToWords(displayTotals.totalAmount || 0);

  return (
    <div className="space-y-6" data-testid="invoice-preview-page">
      {/* Phase 6 migration: action bar hidden in printMode so the PDF
          render via /invoice-preview/:invoiceNumber doesn't bake the
          Back / Download buttons into the output. */}
      {!printMode && (
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="invoice-back-btn"><ArrowLeft className="h-4 w-4" /></Button>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Invoice Preview</h1>
        </div>
        <Button onClick={handleDownloadPDF} disabled={pdfLoading} data-testid="invoice-download-pdf-btn" className="bg-teal-600 hover:bg-teal-700 text-white rounded-md">
          <Download className="h-4 w-4 mr-2" />{pdfLoading ? 'Generating…' : 'Download PDF'}
        </Button>
      </div>
      )}

      {/* Printable Invoice */}
      <div ref={printRef} className="max-w-4xl mx-auto bg-white border border-[#E5E7EB] shadow-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>
        {/* Brand stripe (3pt teal accent — matches voucher layout) */}
        <div style={{ height: '4px', background: '#34D0CB' }} />

        {/* Header */}
        <div className="px-10 py-6 bg-white border-b border-[#E5E7EB]">
          <div className="flex justify-between items-start gap-6">
            <div className="flex items-start gap-4">
              {(company.logoUrl || true) && (
                <img
                  src={
                    company.logoUrl
                      ? (company.logoUrl.startsWith('http') ? company.logoUrl : `${BACKEND_URL}${company.logoUrl}`)
                      : `${BACKEND_URL}/api/uploads/travcations-logo.png`
                  }
                  alt={company.name || 'Travcations'}
                  className="h-8 w-auto object-contain"
                  crossOrigin="anonymous"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              )}
              <div>
                {company.name && <p className="text-base font-semibold text-[#111827] leading-tight">{company.name}</p>}
                {company.tagline && <p className="text-[10px] italic text-[#6B7280] mt-0.5">{company.tagline}</p>}
                {company.address && <p className="text-[11px] text-[#6B7280] max-w-xs mt-1" style={{ lineHeight: 1.3 }}>{company.address}</p>}
                <div className="flex gap-3 mt-1 text-[11px] text-[#6B7280] flex-wrap">
                  {company.phone && <span>Tel: {company.phone}</span>}
                  {company.email && <span>{company.email}</span>}
                  {company.website && <span>{company.website}</span>}
                </div>
                {/* FIX 3 — agency GSTIN + PAN (from Settings → Company Details).
                    Rendered only when present so the label never shows blank. */}
                {(company.gstNumber || company.panNumber) && (
                  <div className="flex gap-3 mt-1 text-[10px] text-[#6B7280] flex-wrap font-mono">
                    {company.gstNumber && <span data-testid="invoice-agency-gstin">GSTIN: {company.gstNumber}</span>}
                    {company.panNumber && <span data-testid="invoice-agency-pan">PAN: {company.panNumber}</span>}
                  </div>
                )}
                {/* Phase 17.2 #7 — IATA / TAAI numbers on the invoice
                    header. These are mandatory trade-credential markers
                    for international air-ticket invoicing. */}
                {(company.iataNumber || company.taaiNumber) && (
                  <div className="flex gap-3 mt-1 text-[10px] text-[#6B7280] flex-wrap font-mono">
                    {company.iataNumber && <span>IATA: {company.iataNumber}</span>}
                    {company.taaiNumber && <span>TAAI: {company.taaiNumber}</span>}
                  </div>
                )}
              </div>
            </div>
            <div className="text-right shrink-0">
              <p className="uppercase text-[10px] tracking-[0.2em] font-semibold" style={{ color: '#34D0CB' }}>
                {inv.invoiceType === 'proforma' ? 'Proforma Invoice' : 'Tax Invoice'}
              </p>
              {/* FIX 5 — invoice number on two lines, right-aligned, same column. */}
              <p className="text-[10px] uppercase tracking-[0.15em] text-[#6B7280] mt-2">Invoice No.</p>
              <p className="text-lg font-mono font-semibold leading-tight" style={{ color: '#111827' }} data-testid="invoice-number">{inv.invoiceNumber}</p>
              {(inv.displayTripId || inv.tripId) && (
                <p className="text-xs text-[#6B7280] mt-1" data-testid="invoice-trip-ref">Trip Ref: {inv.displayTripId || inv.tripId}</p>
              )}
              <p className="text-xs text-[#6B7280] mt-1">Date: {formatDate(inv.invoiceDate)}</p>
              {inv.dueDate && <p className="text-xs text-[#6B7280]">Due: {formatDate(inv.dueDate)}</p>}
              {inv.invoiceType === 'proforma' && (
                <p className="text-[9px] text-amber-700 italic mt-2 max-w-[180px] ml-auto leading-snug">
                  This is not a tax invoice — no input tax credit can be claimed against it.
                </p>
              )}
              {inv.convertedFrom && (
                <p className="text-[9px] text-emerald-700 mt-2">Converted from {inv.convertedFrom}</p>
              )}
              {inv.convertedTo && (
                <p className="text-[9px] text-slate-500 mt-2">Superseded by {inv.convertedTo}</p>
              )}
            </div>
          </div>
        </div>

        {/* GST / Supply Info — FIX 2: compact single line (10px, muted). */}
        <div className="px-10 py-1.5 border-b border-[#E5E7EB]" data-testid="invoice-gst-supply-line">
          <p className="text-[10px] text-[#9CA3AF]">
            GST: {Number(inv.gstRate) || 0}% &nbsp;|&nbsp; Supply: {inv.supplyType === 'intrastate' ? 'Intra-State' : 'Inter-State'} &nbsp;|&nbsp; Place: {inv.placeOfSupply || '—'}
          </p>
        </div>

        {/* Bill To — FIX 8: strict line order, nothing else.
            1:{name} | UCC:{ucc}  2:{address}  3:PAN(if any)  4:GST(if any)
            5:{email} | {phone} */}
        <div className="px-10 py-4 border-b border-[#E5E7EB]" data-testid="invoice-bill-to">
          <p className="uppercase text-[9px] tracking-[0.15em] text-[#9CA3AF] font-semibold mb-1.5">
            Bill To{activeSplit ? ' (Split Recipient)' : ''}
          </p>
          <div className="text-xs text-[#6B7280]" style={{ lineHeight: 1.3 }}>
            <p className="text-sm font-medium text-[#1A1A1A]">
              {client.name || 'Guest'}
              {client.displayUCC && (
                <span className="font-normal text-[#6B7280]"> | UCC: <span data-testid="invoice-client-id" className="font-mono">{client.displayUCC}</span></span>
              )}
            </p>
            {client.address && <p data-testid="invoice-client-address">{client.address}</p>}
            {client.panCard && <p data-testid="invoice-client-pan" className="font-mono">PAN: {client.panCard}</p>}
            {client.gstNumber && <p data-testid="invoice-client-gst" className="font-mono">GST: {client.gstNumber}</p>}
            {(client.email || client.phone) && <p>{[client.email, client.phone].filter(Boolean).join(' | ')}</p>}
          </div>
        </div>

        {/* Items Table */}
        <div className="px-10 py-5 border-b border-[#E5E7EB]">
          <table className="w-full" data-testid="invoice-items-table">
            <thead>
              <tr className="border-b border-[#E5E7EB]">
                <th className="text-left py-2 text-[9px] uppercase tracking-[0.1em] text-[#9CA3AF] font-semibold">#</th>
                <th className="text-left py-2 text-[9px] uppercase tracking-[0.1em] text-[#9CA3AF] font-semibold">Description</th>
                {showHsn && (
                  <th className="text-left py-2 text-[9px] uppercase tracking-[0.1em] text-[#9CA3AF] font-semibold">SAC Code</th>
                )}
                <th className="text-right py-2 text-[9px] uppercase tracking-[0.1em] text-[#9CA3AF] font-semibold">Amount (₹)</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => (
                <tr key={item.tripId || `item-${i}`} className="border-b border-[#F3F4F6]">
                  <td className="py-3 text-xs text-[#6B7280]">{i + 1}</td>
                  <td className="py-3">
                    <p className="text-sm font-medium text-[#1A1A1A]">
                      {item.description}
                      {item.productType && (
                        <span
                          className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-teal-50 text-teal-700 border border-teal-200 align-middle"
                          data-testid={`invoice-item-${i}-product-type`}
                        >
                          {item.productType}
                        </span>
                      )}
                    </p>
                    <p className="text-[11px] text-[#9CA3AF] mt-0.5">
                      {item.tripType && `${item.tripType} · `}
                      {item.adults > 0 && `${item.adults} Adult${item.adults !== 1 ? 's' : ''}`}
                      {item.children > 0 && ` + ${item.children} Child`}
                      {item.travelDates && item.travelDates !== ' to ' && ` · ${item.travelDates}`}
                      {item.destination && ` · ${item.destination}`}
                    </p>
                    {item.referenceNumber && (
                      <p className="text-[11px] text-[#6B7280] mt-0.5 font-mono" data-testid={`invoice-item-${i}-reference`}>
                        {(item.productType === 'Misc' ? (item.referenceLabel || 'Reference') : (REFERENCE_LABELS[item.productType] || item.referenceLabel || 'Reference'))}: {item.referenceNumber}
                      </p>
                    )}
                  </td>
                  {showHsn && (
                    <td className="py-3 text-xs text-[#6B7280] font-mono">{item.hsnCode || '998555'}</td>
                  )}
                  <td className="py-3 text-sm font-medium text-[#1A1A1A] text-right font-mono">{fmtNum(item.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Tax Summary */}
        <div className="px-10 py-5 border-b border-[#E5E7EB]">
          <div className="flex justify-end">
            <div className="w-72 space-y-0">
              <TaxRow label="Subtotal" value={fmtNum(displayTotals.baseCost)} />
              {/* F#5 — Service Fee line (agency-commission mode) */}
              {inv.gstOnServiceFee && (inv.serviceFee || 0) > 0 && !activeSplit && (
                <TaxRow label="Service Fee" value={fmtNum(inv.serviceFee)} />
              )}
              {inv.invoiceType === 'proforma' ? (
                /* Proforma: show GST as a single inclusive line, NO breakup */
                displayTotals.gstAmount > 0 && <TaxRow label={`GST (incl., not for ITC)${inv.gstOnServiceFee ? ' on Service Fee' : ''}`} value={fmtNum(displayTotals.gstAmount)} />
              ) : (
                inv.supplyType === 'intrastate' ? (
                  <>
                    <TaxRow label={`CGST @ ${inv.cgstRate}%${inv.gstOnServiceFee ? ' on Service Fee' : ''}`} value={fmtNum(displayTotals.cgstAmount)} />
                    <TaxRow label={`SGST @ ${inv.sgstRate}%${inv.gstOnServiceFee ? ' on Service Fee' : ''}`} value={fmtNum(displayTotals.sgstAmount)} />
                  </>
                ) : (
                  <TaxRow label={`IGST @ ${inv.igstRate}%${inv.gstOnServiceFee ? ' on Service Fee' : ''}`} value={fmtNum(displayTotals.igstAmount)} />
                )
              )}
              {inv.invoiceType !== 'proforma' && <TaxRow label="Subtotal w/ GST" value={fmtNum(displayTotals.subtotal)} />}
              {displayTotals.tcsAmount > 0 && (
                <>
                  <TaxRow label={`TCS @ ${inv.tcsRate}%`} value={fmtNum(displayTotals.tcsAmount)} />
                  {inv.isInternational && (
                    <p className="text-[9px] text-[#9CA3AF] italic mt-1 leading-snug">
                      Tax Collected at Source on overseas tour package — refundable on filing ITR.
                    </p>
                  )}
                </>
              )}
              <div className="flex justify-between py-3 border-t-2 mt-2" style={{ borderColor: '#34D0CB' }}>
                <span className="text-sm font-bold text-[#1A1A1A]">Total</span>
                <span className="text-lg font-bold text-[#1A1A1A] font-mono">₹{fmtNum(displayTotals.totalAmount)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Phase C4 — Split among multiple travellers. Hidden when in
            per-split mode (we're already filtered to a single row). */}
        {!activeSplit && allSplits.length > 0 && (
          <div className="px-10 py-5 border-b border-[#E5E7EB]" data-testid="invoice-splits-table">
            <p className="uppercase text-[9px] tracking-[0.15em] text-[#9CA3AF] font-semibold mb-3">
              Split Among Travellers
            </p>
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#E5E7EB]">
                  <th className="text-left py-2 text-[9px] uppercase tracking-[0.1em] text-[#9CA3AF] font-semibold">Name</th>
                  <th className="text-left py-2 text-[9px] uppercase tracking-[0.1em] text-[#9CA3AF] font-semibold">PAN</th>
                  <th className="text-right py-2 text-[9px] uppercase tracking-[0.1em] text-[#9CA3AF] font-semibold">Base (₹)</th>
                  <th className="text-right py-2 text-[9px] uppercase tracking-[0.1em] text-[#9CA3AF] font-semibold">GST (₹)</th>
                  <th className="text-right py-2 text-[9px] uppercase tracking-[0.1em] text-[#9CA3AF] font-semibold">TCS (₹)</th>
                  <th className="text-right py-2 text-[9px] uppercase tracking-[0.1em] text-[#9CA3AF] font-semibold">Total (₹)</th>
                </tr>
              </thead>
              <tbody>
                {allSplits.map((s, i) => (
                  <tr key={i} className="border-b border-[#F3F4F6]" data-testid={`invoice-split-row-${i}`}>
                    <td className="py-2 text-xs text-[#1A1A1A]">{s.travellerName || '—'}</td>
                    <td className="py-2 text-xs text-[#6B7280] font-mono">{s.panCard || '—'}</td>
                    <td className="py-2 text-xs text-[#1A1A1A] text-right font-mono">{fmtNum(s.amount)}</td>
                    <td className="py-2 text-xs text-[#6B7280] text-right font-mono">{fmtNum(s.gstAmount)}</td>
                    <td className="py-2 text-xs text-[#6B7280] text-right font-mono">{fmtNum(s.tcsAmount)}</td>
                    <td className="py-2 text-xs font-semibold text-[#1A1A1A] text-right font-mono">{fmtNum(s.totalAmount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Amount in Words */}
        <div className="px-10 py-3 border-b border-[#E5E7EB] bg-[#FAFAFA]">
          <p className="text-[10px] uppercase tracking-[0.1em] text-[#9CA3AF] font-semibold">Amount in Words</p>
          <p className="text-sm text-[#1A1A1A] mt-0.5 capitalize">{amountInWords} Only</p>
        </div>

        {/* Bank Details */}
        {bank.bankName && (
          <div className="px-10 py-5 border-b border-[#E5E7EB]">
            <p className="uppercase text-[9px] tracking-[0.15em] text-[#9CA3AF] font-semibold mb-3">Bank Details for Payment</p>
            <div className="grid grid-cols-2 gap-y-2 gap-x-8 text-xs">
              <BankField label="Bank" value={bank.bankName} />
              <BankField label="Account Name" value={bank.accountHolder} />
              <BankField label="Account Number" value={bank.accountNumber} />
              <BankField label="IFSC Code" value={bank.ifscCode} />
            </div>
          </div>
        )}

        {/* Notes */}
        {inv.notes && (
          <div className="px-10 py-4 border-b border-[#E5E7EB]">
            <p className="uppercase text-[9px] tracking-[0.15em] text-[#9CA3AF] font-semibold mb-1">Notes</p>
            <p className="text-xs text-[#6B7280]">{inv.notes}</p>
          </div>
        )}

        {/* F#8 — Terms & Conditions (per-invoice override, falls back to
            companySettings.defaultTermsAndConditions seeded at create time) */}
        {inv.termsAndConditions && (
          <div className="px-10 py-4 border-b border-[#E5E7EB]" data-testid="invoice-tnc-block">
            <p className="uppercase text-[9px] tracking-[0.15em] text-[#9CA3AF] font-semibold mb-1">Terms &amp; Conditions</p>
            <p className="text-xs text-[#6B7280] whitespace-pre-line leading-relaxed">{inv.termsAndConditions}</p>
          </div>
        )}

        {/* Footer */}
        <div className="px-10 py-5 flex justify-between items-end">
          <div className="text-[10px] text-[#9CA3AF]">
            {/* F#7 — fixed footer line, no longer interpolates placeOfSupply */}
            <p>This is a computer-generated invoice and no signature is required.</p>
            <p>All Disputes Subject to jurisdiction of Kolkata, India.</p>
            {company.pdfDisclaimer && (
              <p data-testid="invoice-pdf-disclaimer" className="mt-1 max-w-md">{company.pdfDisclaimer}</p>
            )}
          </div>
          <div className="text-right">
            <p className="text-xs text-[#6B7280] mb-3">For {company.name || 'Travcations LLP'}</p>
            {/* FIX 6 — dedicated stamp / signature area (≥60×60px) with a clear
                border + adequate white space, labelled Authorised Signatory. */}
            <div className="ml-auto w-20 h-20 border border-dashed border-[#9CA3AF] rounded-sm" data-testid="invoice-stamp-area" />
            <p className="text-xs text-[#1A1A1A] font-medium mt-2">Authorised Signatory</p>
          </div>
        </div>

        {/* Bottom brand stripe */}
        <div style={{ height: '4px', background: '#34D0CB' }} />
      </div>
    </div>
  );
};

const TaxRow = ({ label, value }) => (
  <div className="flex justify-between py-1.5 text-sm">
    <span className="text-[#6B7280]">{label}</span>
    <span className="font-mono text-[#1A1A1A]">₹{value}</span>
  </div>
);

const BankField = ({ label, value }) => (
  <div>
    <span className="text-[#9CA3AF]">{label}: </span>
    <span className="text-[#1A1A1A] font-medium font-mono">{value || '—'}</span>
  </div>
);

function fmtNum(n) { return (n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

function formatDate(d) {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return d; }
}

function numberToWords(num) {
  if (num === 0) return 'zero rupees';
  const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
    'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
  const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

  const convert = (n) => {
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    if (n < 1000) return ones[Math.floor(n / 100)] + ' hundred' + (n % 100 ? ' and ' + convert(n % 100) : '');
    if (n < 100000) return convert(Math.floor(n / 1000)) + ' thousand' + (n % 1000 ? ' ' + convert(n % 1000) : '');
    if (n < 10000000) return convert(Math.floor(n / 100000)) + ' lakh' + (n % 100000 ? ' ' + convert(n % 100000) : '');
    return convert(Math.floor(n / 10000000)) + ' crore' + (n % 10000000 ? ' ' + convert(n % 10000000) : '');
  };

  const rupees = Math.floor(num);
  const paise = Math.round((num - rupees) * 100);
  let result = 'rupees ' + convert(rupees);
  if (paise > 0) result += ' and ' + convert(paise) + ' paise';
  return result;
}

export default InvoicePreview;
